import React, { useEffect, useState, useCallback, useRef } from 'react';
import { api, trackEvent } from './App';
import { ChevronDown, ArrowRight, ArrowLeft, Check, Close } from './Icons';

const SRA_DISCLAIMER = 'SQE Sample Questions and Performance Indicators remain the property of the SRA and the SRA makes no warranty as to the accuracy of their reproduction or usage in these materials.';
const SRA_LINK_TEXT = 'SQE sample questions and Performance Indicators are available on the SQE website at:';
const SRA_LINK_URL = 'https://sqe.sra.org.uk/assessments/sqe1-assessments/sqe1-sample-questions';

const EXAM_SECS_PER_Q = 93; // 1 min 33 sec

const FLK1_SUBJECTS = [
  'Business Law and Practice', 'Dispute Resolution', 'Contract Law', 'Tort',
  'Legal System of England and Wales', 'Legal Services', 'Ethics and Professional Conduct',
];
const FLK2_SUBJECTS = [
  'Property Law and Practice', 'Land Law', 'Wills and Administration of Estates',
  'Solicitors Accounts', 'Trusts Law', 'Criminal Liability', 'Criminal Law and Practice',
  'Ethics and Professional Conduct',
];

function accColor(acc) {
  if (acc === null || acc === undefined) return 'var(--muted)';
  if (acc < 0.5) return 'var(--red)';
  if (acc < 0.7) return 'var(--amber)';
  return 'var(--green)';
}

function AccBadge({ attempted, correct }) {
  if (!attempted) return <span className="sra-acc-badge sra-acc-none">Not started</span>;
  const pct = Math.round((correct / attempted) * 100);
  const color = accColor(correct / attempted);
  return (
    <span className="sra-acc-badge" style={{ color, background: `${color}18`, borderColor: `${color}55` }}>
      {correct}/{attempted} ({pct}%)
    </span>
  );
}

function fmtTime(secs) {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

// ── Question Navigator dots ────────────────────────────────────────────────
function QuestionNav({ total, current, answered, flagged, onJump, mode }) {
  return (
    <div className="sra-q-nav">
      {Array.from({ length: total }, (_, i) => {
        const isAnswered = answered.has(i);
        const isFlagged = flagged && flagged.has(i);
        const isCurrent = i === current;
        let cls = 'sra-q-dot';
        if (isCurrent) cls += ' sra-q-dot-current';
        else if (isAnswered) cls += ' sra-q-dot-answered';
        if (isFlagged) cls += ' sra-q-dot-flagged';
        return (
          <button key={i} className={cls} onClick={() => onJump(i)} title={`Q${i + 1}`} />
        );
      })}
    </div>
  );
}

// ── Mode selector ─────────────────────────────────────────────────────────
function ModeSelect({ questions, onStart, onBack }) {
  const examMins = Math.round((EXAM_SECS_PER_Q * questions.length) / 60);
  return (
    <div className="sra-mode-select">
      <div className="sra-mode-title">Choose your session type</div>
      <div className="sra-mode-cards">
        <div className="sra-mode-card" onClick={() => onStart('practice')}>
          <div className="sra-mode-card-icon">✓</div>
          <div className="sra-mode-card-name">Practice</div>
          <div className="sra-mode-card-desc">
            See whether you're right or wrong immediately after each answer.
            Your time is tracked quietly and shown at the end.
          </div>
          <div className="sra-mode-card-cta">Start practice <ArrowRight size={13} /></div>
        </div>
        <div className="sra-mode-card sra-mode-card-exam" onClick={() => onStart('exam')}>
          <div className="sra-mode-card-icon">⏱</div>
          <div className="sra-mode-card-name">Exam mode</div>
          <div className="sra-mode-card-desc">
            Answer all {questions.length} questions under timed conditions ({examMins} minutes).
            Results revealed only at the end.
          </div>
          <div className="sra-mode-card-cta">Start exam <ArrowRight size={13} /></div>
        </div>
      </div>
      <button className="sra-filter-btn" style={{ marginTop: 12 }} onClick={onBack}>← Back</button>
    </div>
  );
}

// ── Practice view (immediate feedback, hidden timer) ──────────────────────
function PracticeView({ questions, onDone, onBack }) {
  const [idx, setIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [answered, setAnswered] = useState(new Set());
  const [results, setResults] = useState({}); // idx → {is_correct, correct_answer, selected}
  const scrollRef = useRef(null);

  // Timing
  const qStartRef = useRef(Date.now());
  const timesRef = useRef([]); // seconds per question

  useEffect(() => {
    setSelected(null);
    setResult(null);
    qStartRef.current = Date.now();
    scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [idx]);

  const question = questions[idx];

  const selectOption = useCallback(async (label) => {
    if (result || submitting) return;
    setSelected(label);
    setSubmitting(true);
    const elapsed = Math.round((Date.now() - qStartRef.current) / 1000);
    const res = await api('/api/sra/attempt', {
      method: 'POST',
      body: JSON.stringify({ question_id: question.id, selected_answer: label }),
    });
    trackEvent('first_sra_attempt');
    timesRef.current[idx] = elapsed;
    setResult(res);
    setAnswered(prev => new Set([...prev, idx]));
    setResults(prev => ({ ...prev, [idx]: { ...res, selected: label } }));
    setSubmitting(false);
  }, [result, submitting, question, idx]);

  const goTo = (i) => {
    setIdx(i);
  };

  const next = () => {
    // Find next unanswered or advance
    const nextUnanswered = questions.findIndex((_, i) => i > idx && !answered.has(i));
    if (nextUnanswered !== -1) {
      setIdx(nextUnanswered);
    } else if (idx < questions.length - 1) {
      setIdx(i => i + 1);
    } else {
      // All done
      const totalSecs = timesRef.current.reduce((a, b) => a + (b || 0), 0);
      const answeredCount = answered.size + 1; // include current
      onDone({ results, totalSecs, answeredCount, mode: 'practice' });
    }
  };

  const finish = () => {
    const totalSecs = timesRef.current.reduce((a, b) => a + (b || 0), 0);
    onDone({ results, totalSecs, answeredCount: answered.size, mode: 'practice' });
  };

  if (!question) return null;

  return (
    <div className="sra-question-wrap" ref={scrollRef}>
      <div className="sra-q-progress">
        {onBack && answered.size === 0 && (
          <button className="sra-back-btn" onClick={onBack}><ArrowLeft size={13} /> Back</button>
        )}
        <span className="sra-q-counter">{idx + 1} / {questions.length}</span>
        <QuestionNav
          total={questions.length}
          current={idx}
          answered={answered}
          onJump={goTo}
          mode="practice"
        />
        <span className="sra-q-tag">{question.paper} · {question.subject_area}</span>
        <button className="sra-quit-btn" onClick={finish}>Exit</button>
      </div>

      <div className="sra-q-body">
        <div className="sra-q-stem">{question.stem}</div>
        <div className="sra-q-lead-in">{question.lead_in}</div>

        <div className="sra-q-options">
          {(question.options || []).map(opt => {
            let cls = 'sra-option';
            if (result) {
              if (opt.label === result.correct_answer) cls += ' sra-option-correct';
              else if (opt.label === selected && !result.is_correct) cls += ' sra-option-wrong';
              else cls += ' sra-option-disabled';
            } else if (selected === opt.label) {
              cls += ' sra-option-selected sra-option-loading';
            }
            return (
              <button key={opt.label} className={cls}
                disabled={!!result || submitting}
                onClick={() => selectOption(opt.label)}>
                <span className="sra-option-label">{opt.label}</span>
                <span className="sra-option-text">{opt.text}</span>
                {result && opt.label === result.correct_answer && (
                  <span className="sra-option-tick"><Check size={14} /></span>
                )}
              </button>
            );
          })}
        </div>

        {!result ? (
          <div className="sra-practice-hint">Click an answer to check it</div>
        ) : (
          <div className="sra-result">
            <div className={`sra-result-banner ${result.is_correct ? 'sra-result-correct' : 'sra-result-wrong'}`}>
              {result.is_correct
                ? <><Check size={16} /> Correct</>
                : <><Close size={16} /> Incorrect. Correct answer: <strong>{result.correct_answer}</strong></>
              }
            </div>
            {answered.size >= questions.length ? (
              <button className="sra-next-btn sra-finish-btn" onClick={finish}>
                Finish session <ArrowRight size={14} />
              </button>
            ) : (
              <button className="sra-next-btn" onClick={next}>
                Next question <ArrowRight size={14} />
              </button>
            )}
          </div>
        )}
      </div>

      <div className="sra-q-disclaimer">{SRA_DISCLAIMER}</div>
    </div>
  );
}

// ── Exam view (countdown timer, deferred feedback) ────────────────────────
function ExamView({ questions, onDone, onBack }) {
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({}); // idx → label
  const [flagged, setFlagged] = useState(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [timeLeft, setTimeLeft] = useState(EXAM_SECS_PER_Q * questions.length);
  const [autoSubmitted, setAutoSubmitted] = useState(false);
  const scrollRef = useRef(null);
  const timerRef = useRef(null);

  const totalTime = EXAM_SECS_PER_Q * questions.length;

  const submitExam = useCallback(async (currentAnswers) => {
    if (submitting) return;
    setSubmitting(true);
    clearInterval(timerRef.current);
    const attempts = questions.map((q, i) => ({
      question_id: q.id,
      selected_answer: currentAnswers[i] || null,
    }));
    const res = await api('/api/sra/attempts/batch', {
      method: 'POST',
      body: JSON.stringify({ attempts }),
    });
    // Merge question data into results
    const withQuestions = (res.results || []).map((r, i) => ({
      ...r,
      question: questions[i],
    }));
    onDone({ examResults: withQuestions, totalSecs: totalTime - timeLeft, mode: 'exam' });
  }, [submitting, questions, onDone, totalTime, timeLeft]);

  // Countdown timer
  useEffect(() => {
    timerRef.current = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) {
          clearInterval(timerRef.current);
          if (!autoSubmitted) {
            setAutoSubmitted(true);
          }
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, []);

  // Auto-submit when timer hits 0
  useEffect(() => {
    if (autoSubmitted && !submitting) {
      submitExam(answers);
    }
  }, [autoSubmitted, submitting, submitExam, answers]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [idx]);

  const question = questions[idx];
  const answered = new Set(Object.keys(answers).map(Number));
  const answeredCount = answered.size;
  const timerPct = (timeLeft / totalTime) * 100;
  const timerColor = timeLeft < 60 ? 'var(--red)' : timeLeft < totalTime * 0.25 ? 'var(--amber)' : 'var(--green)';

  const selectOption = (label) => {
    setAnswers(prev => ({ ...prev, [idx]: label }));
  };

  const toggleFlag = () => {
    setFlagged(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  const goTo = (i) => setIdx(i);

  const goNext = () => {
    if (idx < questions.length - 1) setIdx(idx + 1);
  };
  const goPrev = () => {
    if (idx > 0) setIdx(idx - 1);
  };

  const jumpNextUnanswered = () => {
    const next = questions.findIndex((_, i) => i !== idx && !answered.has(i));
    if (next !== -1) setIdx(next);
  };

  const unansweredLeft = questions.length - answeredCount;

  return (
    <div className="sra-question-wrap sra-exam-wrap" ref={scrollRef}>
      {/* Exam header: countdown + progress */}
      <div className="sra-exam-header">
        {onBack && answeredCount === 0 && !autoSubmitted && (
          <button className="sra-back-btn" onClick={() => { clearInterval(timerRef.current); onBack(); }}>
            <ArrowLeft size={13} /> Back
          </button>
        )}
        <div className="sra-exam-timer" style={{ color: timerColor }}>
          ⏱ {fmtTime(timeLeft)}
        </div>
        <div className="sra-exam-progress-bar-bg">
          <div className="sra-exam-progress-bar-fill" style={{ width: timerPct + '%', background: timerColor }} />
        </div>
        <span className="sra-q-counter">{idx + 1} / {questions.length}</span>
        <span className="sra-exam-answered">{answeredCount} answered</span>
        <button className="sra-quit-btn" onClick={() => submitExam(answers)} disabled={submitting}>
          {submitting ? 'Submitting…' : 'Submit'}
        </button>
      </div>

      <div className="sra-q-progress">
        <QuestionNav total={questions.length} current={idx} answered={answered} flagged={flagged} onJump={goTo} mode="exam" />
        <span className="sra-q-tag">{question.paper} · {question.subject_area}</span>
        <button
          className={`sra-flag-btn ${flagged.has(idx) ? 'sra-flag-btn-active' : ''}`}
          onClick={toggleFlag}
          title="Flag for review"
        >🚩</button>
      </div>

      <div className="sra-q-body">
        <div className="sra-q-stem">{question.stem}</div>
        <div className="sra-q-lead-in">{question.lead_in}</div>

        <div className="sra-q-options">
          {(question.options || []).map(opt => {
            const isSelected = answers[idx] === opt.label;
            return (
              <button key={opt.label}
                className={`sra-option${isSelected ? ' sra-option-selected' : ''}`}
                onClick={() => selectOption(opt.label)}>
                <span className="sra-option-label">{opt.label}</span>
                <span className="sra-option-text">{opt.text}</span>
              </button>
            );
          })}
        </div>

        <div className="sra-exam-nav-row">
          <button className="sra-nav-btn" onClick={goPrev} disabled={idx === 0}>← Prev</button>
          {unansweredLeft > 1 && (
            <button className="sra-nav-btn sra-nav-skip" onClick={jumpNextUnanswered}>
              Skip to unanswered ({unansweredLeft})
            </button>
          )}
          {idx < questions.length - 1
            ? <button className="sra-nav-btn sra-nav-next" onClick={goNext}>Next →</button>
            : <button className="sra-next-btn" onClick={() => submitExam(answers)} disabled={submitting}>
                {submitting ? 'Submitting…' : <>Finish &amp; submit <ArrowRight size={13} /></>}
              </button>
          }
        </div>
      </div>

      <div className="sra-q-disclaimer">{SRA_DISCLAIMER}</div>
    </div>
  );
}

// ── Exam results summary ──────────────────────────────────────────────────
function ExamSummary({ examResults, totalSecs, onRetake, onBack }) {
  const answered = examResults.filter(r => !r.skipped);
  const correct = answered.filter(r => r.is_correct).length;
  const pct = answered.length > 0 ? Math.round(correct / answered.length * 100) : 0;
  const color = accColor(answered.length > 0 ? correct / answered.length : null);

  return (
    <div className="sra-summary sra-exam-summary">
      <div className="sra-summary-title">Exam complete</div>
      <div className="sra-exam-score-row">
        <span className="sra-exam-score" style={{ color }}>{correct}/{answered.length}</span>
        <span className="sra-exam-pct" style={{ color }}>{pct}%</span>
        <span className="sra-exam-time">⏱ {fmtTime(totalSecs)}</span>
      </div>

      <div className="sra-exam-results-list">
        {examResults.map((r, i) => {
          const q = r.question;
          const bg = r.skipped ? 'var(--bg2)' : r.is_correct ? 'var(--green)18' : 'var(--red)18';
          const border = r.skipped ? 'var(--border)' : r.is_correct ? 'var(--green)55' : 'var(--red)55';
          return (
            <div key={i} className="sra-exam-result-row" style={{ background: bg, borderColor: border }}>
              <div className="sra-exam-result-num">Q{i + 1}</div>
              <div className="sra-exam-result-body">
                <div className="sra-exam-result-lead">{q?.lead_in}</div>
                <div className="sra-exam-result-meta">
                  {r.skipped
                    ? <span style={{ color: 'var(--muted)' }}>Not answered · Correct: <strong>{r.correct_answer}</strong></span>
                    : r.is_correct
                      ? <span style={{ color: 'var(--green)' }}><Check size={12} /> Your answer: <strong>{r.selected_answer}</strong></span>
                      : <span style={{ color: 'var(--red)' }}><Close size={12} /> Your answer: <strong>{r.selected_answer}</strong> · Correct: <strong>{r.correct_answer}</strong></span>
                  }
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="sra-summary-actions">
        <button className="sra-start-btn" onClick={onRetake}>Retake same set</button>
        <button className="sra-filter-btn" onClick={onBack}>Back to overview</button>
      </div>

      <div className="sra-disclaimer-box" style={{ marginTop: 24 }}>
        <div className="sra-disclaimer-text">{SRA_DISCLAIMER}</div>
      </div>
    </div>
  );
}

// ── Practice session summary ──────────────────────────────────────────────
function PracticeSummary({ sessionData, onRetake, onBack }) {
  const { results, totalSecs, answeredCount, questions: qs } = sessionData;
  const resultArr = Object.values(results || {});
  const correct = resultArr.filter(r => r.is_correct).length;
  const avgSecs = answeredCount > 0 ? Math.round(totalSecs / answeredCount) : 0;
  const color = resultArr.length > 0 ? accColor(correct / resultArr.length) : 'var(--muted)';

  return (
    <div className="sra-summary sra-exam-summary">
      <div className="sra-summary-title">Session complete</div>
      <p className="sra-summary-sub">Your results have been saved and will update your priority dashboard.</p>

      <div className="sra-exam-score-row">
        {resultArr.length > 0 && (
          <span className="sra-exam-score" style={{ color }}>
            {correct}/{resultArr.length}
          </span>
        )}
        {resultArr.length > 0 && (
          <span className="sra-exam-pct" style={{ color }}>
            {Math.round(correct / resultArr.length * 100)}%
          </span>
        )}
        {avgSecs > 0 && (
          <span className="sra-exam-time">⏱ avg {fmtTime(avgSecs)} / question</span>
        )}
      </div>

      {/* Per-question breakdown */}
      {qs && qs.length > 0 && (
        <div className="sra-exam-results-list">
          {qs.map((q, i) => {
            const r = results?.[i];
            if (!r) return null; // skipped/unanswered questions
            const bg = r.is_correct ? 'var(--green)18' : 'var(--red)18';
            const border = r.is_correct ? 'var(--green)55' : 'var(--red)55';
            return (
              <div key={i} className="sra-exam-result-row" style={{ background: bg, borderColor: border }}>
                <div className="sra-exam-result-num">Q{i + 1}</div>
                <div className="sra-exam-result-body">
                  <div className="sra-exam-result-lead">{q.lead_in}</div>
                  <div className="sra-exam-result-meta">
                    {r.is_correct
                      ? <span style={{ color: 'var(--green)' }}><Check size={12} /> Your answer: <strong>{r.selected}</strong></span>
                      : <span style={{ color: 'var(--red)' }}><Close size={12} /> Your answer: <strong>{r.selected}</strong> · Correct: <strong>{r.correct_answer}</strong></span>
                    }
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="sra-summary-actions">
        <button className="sra-start-btn" onClick={onRetake}>Retake same set</button>
        <button className="sra-filter-btn" onClick={onBack}>Back to overview</button>
      </div>

      <div className="sra-disclaimer-box" style={{ marginTop: 24 }}>
        <div className="sra-disclaimer-text">{SRA_DISCLAIMER}</div>
      </div>
    </div>
  );
}

// ── Paper/filter components ───────────────────────────────────────────────
function PaperCard({ paper, stats, onStart, onFilter }) {
  const paperStats = {
    total: paper === 'FLK1' ? stats.flk1_total : stats.flk2_total,
    attempted: paper === 'FLK1' ? stats.flk1_attempted : stats.flk2_attempted,
    correct: paper === 'FLK1' ? stats.flk1_correct : stats.flk2_correct,
  };
  const pct = paperStats.attempted > 0 ? Math.round(paperStats.correct / paperStats.attempted * 100) : null;
  const color = paper === 'FLK1' ? 'var(--flk1)' : 'var(--flk2)';
  const attemptedPct = paperStats.total > 0 ? Math.round(paperStats.attempted / paperStats.total * 100) : 0;

  return (
    <div className="sra-paper-card" style={{ borderColor: color }}>
      <div className="sra-paper-header">
        <span className="sra-paper-label" style={{ color }}>{paper}</span>
        <span className="sra-paper-count">{paperStats.total} questions</span>
      </div>
      <div className="sra-paper-progress">
        <div className="sra-progress-bar-bg">
          <div className="sra-progress-bar-fill" style={{ width: attemptedPct + '%', background: color }} />
        </div>
        <span className="sra-progress-label">
          {paperStats.attempted}/{paperStats.total} attempted
          {pct !== null && <> · <span style={{ color: accColor(paperStats.correct / paperStats.attempted) }}>{pct}% correct</span></>}
        </span>
      </div>
      <div className="sra-paper-actions">
        <button className="sra-start-btn" style={{ background: color }} onClick={() => onStart(paper, null, 'unanswered')}>
          {paperStats.attempted === 0 ? 'Start' : 'Continue'} {paper} <ArrowRight size={13} />
        </button>
        <button className="sra-filter-btn" onClick={() => onFilter(paper)}>
          <ChevronDown size={11} /> Filter
        </button>
      </div>
    </div>
  );
}

function SubjectFilter({ paper, subjects, onStart, onClose }) {
  const [filter, setFilter] = useState('all');
  const subjectList = paper === 'FLK1' ? FLK1_SUBJECTS : FLK2_SUBJECTS;
  const color = paper === 'FLK1' ? 'var(--flk1)' : 'var(--flk2)';
  const availableSubjects = subjectList.filter(s => subjects.some(r => r.subject_area === s));

  return (
    <div className="sra-filter-panel">
      <div className="sra-filter-header">
        <span style={{ color, fontWeight: 600 }}>{paper}: choose questions</span>
        <button className="sra-filter-close" onClick={onClose}><Close size={13} /></button>
      </div>
      <div className="sra-filter-modes">
        {[
          { value: 'all', label: 'All questions' },
          { value: 'unanswered', label: 'Unanswered only' },
          { value: 'incorrect', label: 'Previously incorrect' },
        ].map(m => (
          <button key={m.value}
            className={`sra-mode-btn ${filter === m.value ? 'active' : ''}`}
            onClick={() => setFilter(m.value)}>{m.label}</button>
        ))}
      </div>
      <div className="sra-subject-list">
        <button className="sra-subject-btn" onClick={() => onStart(paper, null, filter)}>
          All {paper} subjects <ArrowRight size={12} />
        </button>
        {availableSubjects.map(s => {
          const data = subjects.find(r => r.subject_area === s);
          return (
            <button key={s} className="sra-subject-btn" onClick={() => onStart(paper, s, filter)}>
              <span>{s}</span>
              {data && <AccBadge attempted={data.attempted} correct={data.correct} />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────
export default function SRAQuestions() {
  const [stats, setStats] = useState(null);
  const [view, setView] = useState('index'); // index | filter | mode | practice | exam | practice-summary | exam-summary
  const [filterPaper, setFilterPaper] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sessionData, setSessionData] = useState(null); // practice summary data
  const [examData, setExamData] = useState(null);       // exam summary data
  const [lastConfig, setLastConfig] = useState(null);   // {paper, subjectArea, filter}

  const loadStats = useCallback(() => {
    api('/api/sra/stats').then(setStats);
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);

  const loadQuestions = useCallback(async (paper, subjectArea, filter) => {
    setLoading(true);
    setError('');
    setLastConfig({ paper, subjectArea, filter });
    let url = `/api/sra/questions?paper=${paper}&filter=${filter || 'all'}`;
    if (subjectArea) url += `&subject_area=${encodeURIComponent(subjectArea)}`;
    const qs = await api(url);
    setLoading(false);
    if (!Array.isArray(qs) || qs.length === 0) {
      setError('No questions found for the selected filter. Try "All questions" instead.');
      return false;
    }
    setQuestions(qs);
    return true;
  }, []);

  // Called from PaperCard or SubjectFilter → load questions then show mode select
  const startSession = useCallback(async (paper, subjectArea, filter) => {
    const ok = await loadQuestions(paper, subjectArea, filter);
    if (ok) setView('mode');
  }, [loadQuestions]);

  const openFilter = (paper) => {
    setFilterPaper(paper);
    setView('filter');
  };

  const retake = useCallback(async () => {
    if (!lastConfig) return;
    const ok = await loadQuestions(lastConfig.paper, lastConfig.subjectArea, lastConfig.filter);
    if (ok) setView('mode');
  }, [lastConfig, loadQuestions]);

  const onPracticeDone = (data) => {
    loadStats();
    setSessionData({ ...data, questions });
    setView('practice-summary');
  };

  const onExamDone = (data) => {
    loadStats();
    setExamData(data);
    setView('exam-summary');
  };

  // ── Routing ──
  if (view === 'practice') {
    return <PracticeView questions={questions} onDone={onPracticeDone} onBack={() => setView('mode')} />;
  }

  if (view === 'exam') {
    return <ExamView questions={questions} onDone={onExamDone} onBack={() => setView('mode')} />;
  }

  if (view === 'practice-summary') {
    return (
      <PracticeSummary
        sessionData={sessionData}
        onRetake={retake}
        onBack={() => setView('index')}
      />
    );
  }

  if (view === 'exam-summary') {
    return (
      <ExamSummary
        examResults={examData?.examResults || []}
        totalSecs={examData?.totalSecs || 0}
        onRetake={retake}
        onBack={() => setView('index')}
      />
    );
  }

  if (view === 'mode') {
    return (
      <ModeSelect
        questions={questions}
        onStart={(mode) => setView(mode)}
        onBack={() => setView('index')}
      />
    );
  }

  // ── Index + filter view ──
  return (
    <div className="sra-index">
      <div className="sra-index-header">
        <div className="sra-index-title">SRA Sample Questions</div>
        <div className="sra-index-sub">Official SQE1 sample questions mapped to your revision topics</div>
      </div>

      {stats && (
        <div className="sra-overview-stats">
          <div className="sra-stat">
            <span className="sra-stat-val">{stats.total_questions}</span>
            <span className="sra-stat-label">total questions</span>
          </div>
          <div className="sra-stat">
            <span className="sra-stat-val">{stats.attempted}</span>
            <span className="sra-stat-label">attempted</span>
          </div>
          <div className="sra-stat">
            <span className="sra-stat-val" style={{ color: stats.attempted > 0 ? accColor(stats.correct / stats.attempted) : 'var(--muted)' }}>
              {stats.attempted > 0 ? Math.round(stats.correct / stats.attempted * 100) + '%' : '—'}
            </span>
            <span className="sra-stat-label">correct</span>
          </div>
          <div className="sra-stat">
            <span className="sra-stat-val">{stats.total_questions - stats.attempted}</span>
            <span className="sra-stat-label">remaining</span>
          </div>
        </div>
      )}

      {error && (
        <div className="sra-error">
          <Close size={13} /> {error}
          <button onClick={() => setError('')} style={{ marginLeft: 8 }}><Close size={11} /></button>
        </div>
      )}

      {view === 'filter' && filterPaper && stats ? (
        <SubjectFilter
          paper={filterPaper}
          subjects={stats.by_subject.filter(s => s.paper === filterPaper)}
          onStart={startSession}
          onClose={() => setView('index')}
        />
      ) : (
        stats && (
          <div className="sra-papers">
            <PaperCard paper="FLK1" stats={stats} onStart={startSession} onFilter={openFilter} />
            <PaperCard paper="FLK2" stats={stats} onStart={startSession} onFilter={openFilter} />
          </div>
        )
      )}

      {loading && <div className="loading" style={{ marginTop: 16 }}>Loading questions…</div>}

      {stats && stats.by_subject && stats.by_subject.some(s => s.attempted > 0) && (
        <div className="sra-by-subject">
          <div className="sra-section-label">Performance by subject area</div>
          {['FLK1', 'FLK2'].map(paper => {
            const subs = stats.by_subject.filter(s => s.paper === paper && s.attempted > 0);
            if (!subs.length) return null;
            const color = paper === 'FLK1' ? 'var(--flk1)' : 'var(--flk2)';
            return (
              <div key={paper} className="sra-subject-group">
                <div className="sra-subject-paper-label" style={{ color }}>{paper}</div>
                {subs.map(s => {
                  const pct = Math.round(s.correct / s.attempted * 100);
                  const acc = s.correct / s.attempted;
                  return (
                    <div key={s.subject_area} className="sra-subject-row">
                      <span className="sra-subject-name">{s.subject_area}</span>
                      <div className="sra-subject-bar-bg">
                        <div className="sra-subject-bar-fill" style={{ width: pct + '%', background: accColor(acc) }} />
                      </div>
                      <span className="sra-subject-pct" style={{ color: accColor(acc) }}>{s.correct}/{s.attempted}</span>
                      <span className="sra-subject-pct-label" style={{ color: accColor(acc) }}>{pct}%</span>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      <div className="sra-disclaimer-box">
        <div className="sra-disclaimer-text">{SRA_DISCLAIMER}</div>
        <div className="sra-disclaimer-link">
          {SRA_LINK_TEXT}{' '}
          <a href={SRA_LINK_URL} target="_blank" rel="noopener noreferrer">{SRA_LINK_URL}</a>
        </div>
      </div>
    </div>
  );
}
